import React, { useEffect, useRef, useState } from 'react';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { Button } from 'reactstrap';
import authService from '../api-authorization/AuthorizeService';
import { Breadcrumbs } from '../General/Breadcrumbs';
import { ChatRichContent } from './ChatRichContent';
import './OrseroChat.css';

const welcomeMessage = {
    role: 'assistant',
    content: 'Hola. Soy el asistente de Orsero Tracking. Puedo ayudarte a consultar y analizar la información disponible.',
    localOnly: true
};

const maxConversationCharacters = 28000;
const chatSessionStoragePrefix = 'orsero-tracking:chat';
const legacyChatSessionStoragePrefix = 'orsero-tracking:operational-chat';
const chatSessionChannelName = 'orsero-tracking:chat-sync';
const maxStoredMessages = 60;

export function OrseroChat({ standalone = false }) {
    const [messages, setMessages] = useState([welcomeMessage]);
    const [sessionReady, setSessionReady] = useState(false);
    const [draft, setDraft] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState('');
    const [balance, setBalance] = useState(null);
    const [balanceLoading, setBalanceLoading] = useState(true);
    const messagesContainerRef = useRef(null);
    const messagesRef = useRef(messages);
    const storageKeyRef = useRef('');
    const channelRef = useRef(null);
    const lastUpdatedRef = useRef(0);
    const skipPersistenceRef = useRef(false);
    const instanceIdRef = useRef(createInstanceId());

    useEffect(() => {
        let active = true;
        let channel = null;
        let initialSyncTimer = null;

        function completeSessionInitialization() {
            if (initialSyncTimer) {
                window.clearTimeout(initialSyncTimer);
                initialSyncTimer = null;
            }
            if (active) {
                setSessionReady(true);
            }
        }

        async function initializeChatSession() {
            let profile;
            try {
                profile = await authService.getUser();
            } catch {
                if (active) {
                    setSessionReady(true);
                }
                return;
            }

            if (!active) {
                return;
            }

            const storageKey = getChatSessionStorageKey(profile);
            if (!storageKey) {
                setSessionReady(true);
                return;
            }

            storageKeyRef.current = storageKey;
            const legacyStorageKey = getChatSessionStorageKey(
                profile,
                legacyChatSessionStoragePrefix);
            const restoredConversation = readChatSession(storageKey)
                || readChatSession(legacyStorageKey);
            if (restoredConversation) {
                messagesRef.current = restoredConversation.messages;
                lastUpdatedRef.current = restoredConversation.updatedAt;
                writeChatSession(storageKey, restoredConversation);
                setMessages(restoredConversation.messages);
            } else {
                skipPersistenceRef.current = true;
            }

            if ('BroadcastChannel' in window) {
                channel = new window.BroadcastChannel(chatSessionChannelName);
                channelRef.current = channel;
                channel.onmessage = event => {
                    const data = event.data;
                    if (!data
                        || data.storageKey !== storageKey
                        || data.sourceId === instanceIdRef.current) {
                        return;
                    }

                    if (data.type === 'request-state') {
                        channel.postMessage({
                            type: 'conversation-state',
                            storageKey,
                            sourceId: instanceIdRef.current,
                            conversation: {
                                updatedAt: lastUpdatedRef.current || Date.now(),
                                messages: messagesRef.current
                            }
                        });
                        return;
                    }

                    if (data.type !== 'conversation-state') {
                        return;
                    }

                    const remoteConversation = normalizeStoredConversation(data.conversation);
                    if (!remoteConversation) {
                        return;
                    }

                    if (remoteConversation.updatedAt > lastUpdatedRef.current) {
                        lastUpdatedRef.current = remoteConversation.updatedAt;
                        messagesRef.current = remoteConversation.messages;
                        skipPersistenceRef.current = true;
                        writeChatSession(storageKey, remoteConversation);
                        setMessages(remoteConversation.messages);
                    }
                    completeSessionInitialization();
                };

                channel.postMessage({
                    type: 'request-state',
                    storageKey,
                    sourceId: instanceIdRef.current
                });
                initialSyncTimer = window.setTimeout(completeSessionInitialization, 500);
            } else {
                completeSessionInitialization();
            }
        }

        initializeChatSession();
        return () => {
            active = false;
            if (initialSyncTimer) {
                window.clearTimeout(initialSyncTimer);
            }
            channel?.close();
            if (channelRef.current === channel) {
                channelRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        messagesRef.current = messages;
        const storageKey = storageKeyRef.current;
        if (!sessionReady || !storageKey) {
            return;
        }

        if (skipPersistenceRef.current) {
            skipPersistenceRef.current = false;
            return;
        }

        const conversation = {
            updatedAt: Date.now(),
            messages: normalizeMessages(messages)
        };
        lastUpdatedRef.current = conversation.updatedAt;
        writeChatSession(storageKey, conversation);
        channelRef.current?.postMessage({
            type: 'conversation-state',
            storageKey,
            sourceId: instanceIdRef.current,
            conversation
        });
    }, [messages, sessionReady]);

    useEffect(() => {
        let active = true;

        async function loadBalance() {
            try {
                const token = await authService.getAccessToken();
                const response = await fetch('api/chat/balance', {
                    headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                });
                const responseBody = await response.json().catch(() => null);

                if (!response.ok) {
                    throw new Error(responseBody?.detail || responseBody?.title);
                }

                if (active) {
                    setBalance(responseBody);
                }
            } catch {
                if (active) {
                    setBalance(null);
                }
            } finally {
                if (active) {
                    setBalanceLoading(false);
                }
            }
        }

        loadBalance();
        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        const container = messagesContainerRef.current;
        if (container) {
            container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        }
    }, [messages, isSending]);

    useEffect(() => {
        if (!standalone) {
            return undefined;
        }

        const previousTitle = document.title;
        document.title = 'Asistente Orsero | Orsero Tracking';

        return () => {
            document.title = previousTitle;
        };
    }, [standalone]);

    function startNewConversation() {
        setMessages([welcomeMessage]);
        setDraft('');
        setError('');
    }

    function openChatWindow() {
        const availableWidth = window.screen?.availWidth || window.innerWidth;
        const availableHeight = window.screen?.availHeight || window.innerHeight;
        const width = Math.min(1280, Math.max(680, availableWidth - 80));
        const height = Math.min(900, Math.max(600, availableHeight - 80));
        const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - width) / 2));
        const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - height) / 2));
        const features = [
            'popup=yes',
            `width=${width}`,
            `height=${height}`,
            `left=${left}`,
            `top=${top}`,
            'resizable=yes',
            'scrollbars=yes',
            'noopener=yes',
            'noreferrer=yes'
        ].join(',');

        window.open('/AsistenteOrsero/ventana', '_blank', features);
    }

    function getMessagesForRequest(currentMessages) {
        const selected = [];
        let characterCount = 0;

        for (let index = currentMessages.length - 1; index >= 0; index -= 1) {
            const message = currentMessages[index];
            if (message.localOnly) {
                continue;
            }

            const html = typeof message.html === 'string' ? message.html.trim() : '';
            const content = html
                ? `${message.content}\n\nVisualización mostrada en la respuesta anterior:\n${html}`
                : message.content;
            if (selected.length === 20
                || characterCount + content.length > maxConversationCharacters) {
                break;
            }

            selected.push({ role: message.role, content });
            characterCount += content.length;
        }

        return selected.reverse();
    }

    async function sendMessage(event) {
        event.preventDefault();
        const content = draft.trim();
        if (!content || isSending || !sessionReady) {
            return;
        }

        const userMessage = { role: 'user', content };
        const nextMessages = [...messages, userMessage];
        setMessages(nextMessages);
        setDraft('');
        setError('');
        setIsSending(true);

        try {
            const token = await authService.getAccessToken();
            const response = await fetch('api/chat', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(!token ? {} : { 'Authorization': `Bearer ${token}` })
                },
                body: JSON.stringify({
                    messages: getMessagesForRequest(nextMessages)
                })
            });

            const responseBody = await response.json().catch(() => null);
            if (!response.ok) {
                throw new Error(
                    responseBody?.detail
                    || responseBody?.title
                    || 'No se pudo obtener una respuesta de ChatGPT.');
            }

            if (typeof responseBody?.message !== 'string' || !responseBody.message.trim()) {
                throw new Error('ChatGPT devolvió una respuesta vacía.');
            }

            setMessages(current => [
                ...current,
                {
                    role: 'assistant',
                    content: responseBody.message,
                    html: typeof responseBody.html === 'string' && responseBody.html.trim()
                        ? responseBody.html.trim()
                        : null
                }
            ]);
        } catch (requestError) {
            setMessages(current => current.filter(message => message !== userMessage));
            setDraft(content);
            setError(requestError.message || 'No se pudo enviar el mensaje.');
        } finally {
            setIsSending(false);
        }
    }

    function handleKeyDown(event) {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            event.currentTarget.form?.requestSubmit();
        }
    }

    return (
        <section className={`orsero-chat-page${standalone ? ' standalone' : ''}`}>
            {!standalone && (
                <>
                    <h2>Asistente Orsero</h2>
                    <Breadcrumbs data={[
                        { link: '/AsistenteOrsero', message: 'Flujo de trabajo', active: true },
                        { link: '/AsistenteOrsero', message: 'AsistenteOrsero', active: false }
                    ]} />
                </>
            )}

            <div className={`${standalone ? '' : 'viewContainer-lg '}orsero-chat-container`}>
                <div className="orsero-chat-actions">
                    {standalone && (
                        <h1 className="orsero-chat-window-title">Asistente Orsero</h1>
                    )}
                    <BalanceIndicator balance={balance} loading={balanceLoading} />
                    {!standalone && (
                        <Button
                            type="button"
                            className="AddButton orsero-chat-action-button"
                            onClick={openChatWindow}
                        >
                            <OpenInNewRoundedIcon fontSize="small" />
                            Abrir en nueva ventana
                        </Button>
                    )}
                    <Button
                        type="button"
                        className="AddButton orsero-chat-action-button"
                        onClick={startNewConversation}
                        disabled={isSending || !sessionReady}
                    >
                        <RestartAltRoundedIcon fontSize="small" />
                        Nueva conversación
                    </Button>
                </div>

                <div className="orsero-chat-card">
                    <div ref={messagesContainerRef} className="orsero-chat-messages" aria-live="polite">
                        {messages.map((message, index) => (
                            <article
                                key={`${message.role}-${index}`}
                                className={`orsero-chat-message ${message.role}`}
                            >
                                <span className="orsero-chat-message-author">
                                    {message.role === 'user' ? 'Tú' : 'Asistente'}
                                </span>
                                <div className="orsero-chat-message-body">
                                    <div className="orsero-chat-message-text">{message.content}</div>
                                    <ChatRichContent html={message.html} />
                                </div>
                            </article>
                        ))}

                        {isSending && (
                            <article className="orsero-chat-message assistant pending">
                                <span className="orsero-chat-message-author">Asistente</span>
                                <div className="orsero-chat-message-body">
                                    <div className="orsero-chat-typing" aria-label="ChatGPT está respondiendo">
                                        <i></i><i></i><i></i>
                                    </div>
                                </div>
                            </article>
                        )}
                    </div>

                    {error && <div className="orsero-chat-error" role="alert">{error}</div>}

                    <form className="orsero-chat-composer" onSubmit={sendMessage}>
                        <textarea
                            value={draft}
                            onChange={event => setDraft(event.target.value)}
                            onKeyDown={handleKeyDown}
                            maxLength={4000}
                            rows={2}
                            placeholder="Pregunta por períodos, proveedores, destinos, productos o tendencias…"
                            aria-label="Mensaje para el asistente de Orsero"
                            disabled={isSending || !sessionReady}
                        />
                        <button
                            type="submit"
                            disabled={isSending || !sessionReady || !draft.trim()}
                            aria-label="Enviar mensaje"
                        >
                            <SendRoundedIcon />
                        </button>
                    </form>
                    <small className="orsero-chat-hint">Enter para enviar · Shift + Enter para una nueva línea</small>
                </div>
            </div>
        </section>
    );
}

function BalanceIndicator({ balance, loading }) {
    if (loading) {
        return <span className="orsero-chat-balance loading">Consultando límite…</span>;
    }

    if (!balance?.available) {
        return null;
    }

    const remaining = Number(balance.remainingUsd);
    const spent = Number(balance.spentUsd);
    const limit = Number(balance.limitUsd);
    const details = `Gasto reportado este mes: ${formatUsd(spent)} de ${formatUsd(limit)}. `
        + 'Es una estimación del límite mensual, no el saldo prepago de la cuenta.';

    return (
        <span className="orsero-chat-balance" title={details}>
            Disponible del límite: <strong>{formatUsd(remaining)}</strong>
        </span>
    );
}

function formatUsd(value) {
    return new Intl.NumberFormat('es-CR', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(Number.isFinite(value) ? value : 0);
}

function getChatSessionStorageKey(profile, prefix = chatSessionStoragePrefix) {
    const userId = profile?.sub
        || profile?.nameidentifier
        || profile?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];
    if (!userId) {
        return '';
    }

    const authenticationSession = profile?.sid || profile?.auth_time || 'current';
    return `${prefix}:${String(userId)}:${String(authenticationSession)}`;
}

function readChatSession(storageKey) {
    try {
        const serialized = window.sessionStorage.getItem(storageKey);
        return serialized ? normalizeStoredConversation(JSON.parse(serialized)) : null;
    } catch {
        return null;
    }
}

function writeChatSession(storageKey, conversation) {
    try {
        window.sessionStorage.setItem(storageKey, JSON.stringify(conversation));
    } catch {
        // El chat continúa funcionando aunque el navegador no permita almacenamiento de sesión.
    }
}

function normalizeStoredConversation(value) {
    const updatedAt = Number(value?.updatedAt);
    if (!Number.isFinite(updatedAt) || !Array.isArray(value?.messages)) {
        return null;
    }

    return {
        updatedAt,
        messages: normalizeMessages(value.messages)
    };
}

function normalizeMessages(value) {
    const normalized = (Array.isArray(value) ? value : [])
        .filter(message => (message?.role === 'user' || message?.role === 'assistant')
            && typeof message?.content === 'string'
            && message.content.trim())
        .slice(-maxStoredMessages)
        .map(message => ({
            role: message.role,
            content: message.content,
            html: typeof message.html === 'string' && message.html.trim()
                ? message.html.trim()
                : null,
            ...(message.localOnly === true ? { localOnly: true } : {})
        }));

    return normalized.length > 0 ? normalized : [welcomeMessage];
}

function createInstanceId() {
    if (typeof window.crypto?.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
